import * as React from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  ListItemText,
  InputAdornment,
  IconButton,
  MenuProps,
  Typography
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { getCategoryDisplayName } from '../utils/getFilterOptions';
import { setGTMEvent } from '../utils/setGAEvent';
import useStyles from '../styles/componentStyle';
import { FilterFieldProps } from '../typings';

const MENU_ITEM_HEIGHT = 54;
const ITEM_PADDING_TOP = 8;
const MENU_ITEM_WIDTH = 240;
const SelectMenuProps: Partial<MenuProps> = {
  PaperProps: {
    style: {
      maxHeight: MENU_ITEM_HEIGHT * 5 + ITEM_PADDING_TOP,
      width: MENU_ITEM_WIDTH,
      marginTop: MENU_ITEM_HEIGHT + ITEM_PADDING_TOP
    }
  },
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'left'
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left'
  },
  getContentAnchorEl: null
};

const FilterField: React.FC<FilterFieldProps> = (fieldProps) => {
  const { filterKey, selectedFilters, filterOptions, fieldLabel, filterActions, onFilterChange } = fieldProps;

  const { selectAll, clearAll } = filterActions;

  const labelID = `${filterKey}-filter-label`;
  const selectedFiltersLocale: string = selectedFilters.length
    ? getCategoryDisplayName(selectedFilters, filterOptions).join(', ')
    : fieldLabel;

  const [filterValue, setFilterValue] = React.useState<string[]>(selectedFilters);
  const [showOptions, setShowOptions] = React.useState<boolean>(false);

  const handleFilterChange = (event: React.ChangeEvent<{ value: any }>) => {
    const {
      target: { value }
    } = event;
    let filters = value;
    if (value.includes('0')) {
      filters = [];
    } else if (value.includes('1')) {
      filters = filterOptions.map((filter) => filter.name);
    }

    onFilterChange(filterKey, filters);
    setFilterValue(filters);
    setGTMEvent(`filter.${filterKey}`, 'select_content', filters);
  };

  const handleClearClick = () => {
    onFilterChange(filterKey, []);
    setFilterValue([]);
  };

  const handleDropdown = () => {
    const status = !showOptions;
    setShowOptions(status);
  };

  React.useEffect(() => {
    const filters = selectedFilters.length ? selectedFilters : [];
    setFilterValue(filters);
  }, [selectedFilters]);

  const classes = useStyles();

  return (
    <FormControl fullWidth variant='outlined' className={classes.formControl}>
      <InputLabel id={labelID}>{fieldLabel}</InputLabel>
      {selectedFilters.length ? (
        <InputAdornment position='end' className={classes.clearAllIcon}>
          <IconButton aria-label='clear' onClick={handleClearClick}>
            <CancelIcon />
          </IconButton>
        </InputAdornment>
      ) : null}
      <Select
        label={fieldLabel}
        labelId={labelID}
        multiple
        value={filterValue}
        onChange={handleFilterChange}
        renderValue={() => selectedFiltersLocale}
        MenuProps={SelectMenuProps}
        classes={{ outlined: classes.selectOutline }}
        onClose={handleDropdown}
        onOpen={handleDropdown}
        open={showOptions}
      >
        {filterOptions?.length && !selectedFilters.length ? (
          <MenuItem
            key={selectAll.value}
            value={selectAll.value}
            className={classes.actionItem}
            classes={{ selected: classes.selectedMenu }}
          >
            <Typography variant='body1' className={classes.actionItemText}>
              {selectAll.label}
            </Typography>
          </MenuItem>
        ) : null}
        {selectedFilters.length ? (
          <MenuItem
            key={clearAll.value}
            value={clearAll.value}
            className={classes.actionItem}
            classes={{ selected: classes.selectedMenu }}
            onClick={handleDropdown}
          >
            <Typography variant='body1' className={classes.actionItemText}>
              {clearAll.label}
            </Typography>
          </MenuItem>
        ) : null}
        {filterOptions?.map((filterOption) => (
          <MenuItem key={filterOption.name} value={filterOption.name} classes={{ selected: classes.selectedMenu }}>
            <Checkbox checked={selectedFilters.indexOf(filterOption.name) > -1} color='primary' />
            <ListItemText primary={filterOption.displayName} className={classes.menuItem} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FilterField;
